import {
  createContext,
  type FC,
  type ReactNode,
  useEffect,
  useMemo,
} from "react";

import { useMetricSelectorOptions } from "src/components/analytics/shared/metric-selector/use-metric-selector-options";
import type { ColumnOption } from "src/components/explore/visual/utils";
import { Form } from "src/components/form";
import {
  type SavedAnalyticsChartQuery,
  useSavedAnalyticsChartQuery,
} from "src/graphql";
import { usePersonalization } from "src/hooks/use-personalization";
import { useMeasurementColumns } from "src/pages/analytics/hooks/use-measurement-columns";
import { useMeasuringMetricResources } from "src/pages/analytics/hooks/use-measuring-metric-resources";
import type { AnalyticsContextType } from "src/pages/analytics/types";
import { useParams } from "src/router";
import {
  defaultActions,
  defaultFormState,
  defaultSchemaState,
} from "./constants";
import { useChartDependencies } from "./use-chart-dependencies";
import { useChartState } from "./use-chart-state";
import {
  getChartStateFromChartDefinition,
  getSyntheticColumnValuesGetter,
} from "./utils";

type Props = {
  children: ReactNode;
  columns?: ColumnOption[];
  chartId?: string;
  useUrlState?: boolean;
  chart?: SavedAnalyticsChartQuery["saved_analytics_charts_by_pk"];
};

const defaultContextValue: AnalyticsContextType = {
  ...defaultSchemaState,
  ...defaultActions,
};

export const AnalyticsContext =
  createContext<AnalyticsContextType>(defaultContextValue);
AnalyticsContext.displayName = "AnalyticsContext";

const formatSavedChartData = (
  savedChart:
    | SavedAnalyticsChartQuery["saved_analytics_charts_by_pk"]
    | undefined
    | null,
) => {
  if (savedChart) {
    return {
      ...savedChart,
      lookbackWindow: savedChart.lookback_window,
      parentModelId: savedChart.parent_model?.id,
      groupBy: savedChart.group_by,
    };
  }

  return savedChart;
};

export const AnalyticsFormStateProvider: FC<Readonly<Props>> = ({
  children,
  chartId,
  chart,
  useUrlState = true,
}) => {
  const { id } = useParams<{ id: string }>();
  const savedChartId = chartId ?? id;
  const isSavedView = Boolean(savedChartId) && savedChartId !== "new";

  const fastQueryPreference = usePersonalization<boolean>({
    key: "charts.fast-query",
    initialValue: true,
  });

  const { form, ...value } = useChartState({
    useUrlState,
  });

  const savedAnalyticsChartQuery = useSavedAnalyticsChartQuery(
    { id: savedChartId ?? "" },
    {
      enabled: isSavedView,
      select: (data) => data.saved_analytics_charts_by_pk,
      suspense: true,
    },
  );

  const savedChart = useMemo(
    () => formatSavedChartData(chart ?? savedAnalyticsChartQuery.data),
    [chart, savedAnalyticsChartQuery.data],
  );

  const parentModelId = form.watch("parentModelId");
  const measuringSelection = form.watch("measuringSelection");
  const measuringMode = form.watch("measuringMode");

  const chartDependencies = useChartDependencies({
    parentModelId,
  });

  const metricSelectorOptions = useMetricSelectorOptions({
    parentModelId: parentModelId?.toString() ?? null,
    includePredefinedMetrics: true,
  });

  const {
    columnOptions,
    additionalUserColumns,
    isLoading: isMeasurementColumnsLoading,
  } = useMeasurementColumns(measuringSelection, measuringMode);

  const measuringMetricResources = useMeasuringMetricResources({
    measuringSelection,
  });

  const syntheticColumnValuesGetter = getSyntheticColumnValuesGetter(
    measuringSelection,
    measuringMetricResources,
  );

  // This selects the parent model on the form if it's not already selected
  // If it's a saved chart, select the parent model from the saved chart
  // Otherwise, select the first parent model from the available options
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (savedChart?.parentModelId) {
      value.applySavedParentModel(savedChart.parentModelId);
      return;
    }

    // Set parent model as the initial selection if none already selected
    if (!parentModelId && chartDependencies.parentModelOptions) {
      const firstEnabledParentModelOption =
        chartDependencies.parentModelOptions.find((option) => !option.disabled);

      if (firstEnabledParentModelOption) {
        value.selectParentModel(firstEnabledParentModelOption.value);
      }
    }
  }, [chartDependencies.parentModelOptions, parentModelId, savedChart]);

  // This hook fires after the parent model is selected, and fills in all the other parts of the form state
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (
      !savedChart ||
      chartDependencies.dependenciesLoading ||
      !parentModelId ||
      parentModelId !== savedChart.parentModelId
    ) {
      return;
    }

    const savedChartState = getChartStateFromChartDefinition(
      {
        ...savedChart,
        parent_model_id: savedChart.parentModelId,
      },
      chartDependencies,
    );

    if (savedChartState) {
      form.reset({
        ...defaultFormState,
        ...savedChartState,
        parentModelId: savedChart.parentModelId,
      });
    }
  }, [savedChart, chartDependencies.dependenciesLoading, parentModelId]);

  return (
    <AnalyticsContext.Provider
      value={{
        ...value,
        audiences: chartDependencies.audienceOptions,
        metrics: chartDependencies.metricOptions,
        events: chartDependencies.events,
        traits: chartDependencies.traits,
        parent: chartDependencies.parentModel,
        relatedModels: chartDependencies.relatedModels,
        measurementsOptions: chartDependencies.measurementOptions,
        subsetCategoryOptions: chartDependencies.subsetCategoryOptions,
        measurementColumnOptions: columnOptions,
        additionalUserColumns,
        measuringMetricResources,
        syntheticColumnValuesGetter,
        dependenciesLoading: chartDependencies.dependenciesLoading,
        parentModelLoading: chartDependencies.parentModelLoading,
        parentModelOptions: chartDependencies.parentModelOptions,
        parentModelOptionsLoading: chartDependencies.parentModelOptionsLoading,
        measurementsOptionsLoading:
          chartDependencies.measurementsOptionsLoading,
        measurementColumnsLoading: isMeasurementColumnsLoading,
        metricSelectorOptions,
        fastQueryPreference,
        isArchived: Boolean(savedChart && savedChart.archived_at !== null),
        isSavedView,
        savedChartId: savedChart?.id,
        reportSchedule: savedChart?.analytics_report,
      }}
    >
      <Form form={form}>{children}</Form>
    </AnalyticsContext.Provider>
  );
};
